export class RecoveryDataError extends Error {
    constructor(code) {
        super(code)

        this.name = 'RecoveryDataError'
        this.code = code
    }
}

export const hasValue = (value) => value !== null && value !== undefined && value !== ''

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

export const isAmount = (value) =>
    hasValue(value) &&
    ['number', 'string'].includes(typeof value) &&
    Number.isFinite(Number(value)) &&
    Number(value) >= 0

export const toMinorUnits = (value) => Math.round(Number(value) * 100)

export const mapRecoveryData = (payload) => {
    if (
        !isObject(payload) ||
        !['available', 'price_changed'].includes(payload.status) ||
        !isObject(payload.data)
    ) {
        throw new RecoveryDataError('recovery.invalidData')
    }

    const data = payload.data

    if (
        data.recoveryMode !== true ||
        typeof data.checkoutRecovery !== 'string' ||
        !data.checkoutRecovery ||
        typeof data.uddsSnapshot !== 'string' ||
        !data.uddsSnapshot ||
        typeof data.offerHash !== 'string' ||
        !data.offerHash ||
        typeof data.operator !== 'string' ||
        !data.operator ||
        !isObject(data.offer) ||
        !isObject(data.form) ||
        !isObject(data.choices) ||
        !Array.isArray(data.form.adults) ||
        !Array.isArray(data.form.children) ||
        !Array.isArray(data.choices.promotionCodes) ||
        !data.choices.promotionCodes.every(
            (code) => typeof code === 'string' && code.trim() !== '',
        ) ||
        !isAmount(payload.originalOfferPrice) ||
        typeof payload.currency !== 'string' ||
        !payload.currency
    ) {
        throw new RecoveryDataError('recovery.invalidData')
    }

    const adults = Number(data.dorosli)
    const childrenAges = String(data.dzieci || '')
        .split(',')
        .map((age) => age.trim())
        .filter((age) => age !== '')

    if (
        !Number.isInteger(adults) ||
        adults < 1 ||
        adults !== data.form.adults.length ||
        childrenAges.length !== data.form.children.length ||
        !childrenAges.every((age) => /^\d+$/.test(age)) ||
        !data.form.adults.every(isObject) ||
        !data.form.children.every(isObject)
    ) {
        throw new RecoveryDataError('recovery.invalidData')
    }

    return {
        request: {
            offerHash: data.offerHash,
            operator: data.operator,
            offerType: data.offerType || 'MDS',
            od: data.od,
            do: data.do,
            length: data.length,
            dorosli: adults,
            dzieci: childrenAges.join(','),
            dziecko: childrenAges.length,
            checkout_recovery: data.checkoutRecovery,
            udds_snapshot: data.uddsSnapshot,
        },
        offer: data.offer,
        form: {
            ...data.form,
            adults: data.form.adults.map((person) => ({ ...person })),
            children: data.form.children.map((person) => ({ ...person })),
            agreements: [],
        },
        choices: {
            ...data.choices,
            promotionCodes: [...data.choices.promotionCodes],
        },
        dataUdds: isObject(data.dataUdds) ? data.dataUdds : null,
        originalOfferPrice: Number(payload.originalOfferPrice),
        currency: payload.currency.toUpperCase(),
        fallbackUrl: payload.fallbackUrl || null,
    }
}

export const readRecoveryBootstrap = (documentObject) => {
    const script = documentObject.getElementById('checkout_recovery_data')
    const root = documentObject.getElementById('reservation_container')

    const hasMarker = Boolean(
        root && root.getAttribute('data-checkout-recovery'),
    )

    const hasRecoveryPath = /\/reservation\/checkout-recovery\//.test(
        documentObject.location.pathname,
    )

    if (!script && !hasMarker && !hasRecoveryPath) {
        return {
            enabled: false,
            data: null,
            error: null,
        }
    }

    try {
        if (!script) {
            throw new RecoveryDataError('recovery.invalidData')
        }

        return {
            enabled: true,
            data: mapRecoveryData(JSON.parse(script.textContent)),
            error: null,
        }
    } catch (error) {
        return {
            enabled: true,
            data: null,
            error: 'recovery.invalidData',
        }
    }
}

export const selectSavedInsurance = (response, variant) => {
    if (!hasValue(variant)) {
        return null
    }

    if (!response || !Array.isArray(response.calculations)) {
        throw new RecoveryDataError('recovery.insuranceUnavailable')
    }

    const matches = response.calculations.filter(
        (insurance) => insurance.variant === variant,
    )

    if (matches.length !== 1 || !isAmount(matches[0].totalPrice)) {
        throw new RecoveryDataError('recovery.insuranceUnavailable')
    }

    return matches[0]
}

export const selectSavedParking = (offers, choices) => {
    if (!Array.isArray(offers)) {
        throw new RecoveryDataError('recovery.parkingUnavailable')
    }

    const matchesParking = (offer) =>
        offer &&
        offer.parking &&
        String(offer.parking.id) === String(choices.parkingId)

    const matchesOffer = (offer) =>
        offer &&
        hasValue(choices.parkingOfferId) &&
        String(offer.id) === String(choices.parkingOfferId)

    let candidates

    if (hasValue(choices.parkingId)) {
        candidates = offers.filter(matchesParking)

        const exactMatches = candidates.filter(matchesOffer)

        if (exactMatches.length === 1) {
            candidates = exactMatches
        }
    } else {
        candidates = offers.filter(matchesOffer)
    }

    if (candidates.length !== 1 || !candidates[0].parking || !isAmount(candidates[0].price_online)) {
        throw new RecoveryDataError('recovery.parkingUnavailable')
    }

    return candidates[0]
}
